'use client'

import styled from 'styled-components'

import { ResponsiveImageWrapper } from '@syconium/little-miss-figgy/dist/components/Image/ResponsiveImage'
import {
  fromLg,
  fromMd,
  fromXl,
  untilMd,
} from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import { PageHeroSection } from '../../../../../__generated__/graphql/catalog/graphql'
import { ContentfulStyledImageWrapper } from '../components/ContentfulStyledImage'

const getContentAlignment = (textPlacementMobile: string) => {
  if (textPlacementMobile === 'below') return 'flex-start'
  if (textPlacementMobile === 'center') return 'center'
  return 'flex-end'
}

export const ContentfulPageBannerSectionBackground = styled.div<{
  $backgroundColorDesktop: string | undefined | null
  $backgroundColorMobile: string | undefined | null
  $textPlacementDesktop: string | undefined | null
  $textPlacementMobile: string | undefined | null
}>`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: ${o => getContentAlignment(o.$textPlacementMobile ?? 'bottom')};
  position: relative;

  ${untilMd} {
    background-color: ${o => o.$backgroundColorMobile};
  }

  ${fromMd} {
    background-color: ${o => o.$backgroundColorDesktop};
    flex-direction: ${o => (o.$textPlacementDesktop === 'left' ? 'row-reverse' : 'row')};
    justify-content: ${o => o.$textPlacementDesktop === 'center' && 'center'};
  }
`

export const ContentfulPageBannerSectionContent = styled.div<{
  $textPlacementDesktop: string | undefined | null
  $textPlacementMobile: string | undefined | null
  $endsInLink: boolean
  $hasDisclaimer?: boolean
}>`
  box-sizing: border-box;
  padding: ${o => (o.$textPlacementMobile === 'bottom' ? '0 16px 73px 16px' : '0 16px')};

  ${untilMd} {
    padding-top: ${o => o.$textPlacementMobile === 'below' && '48px'};
    width: 100%;
    position: ${o => o.$textPlacementMobile !== 'below' && 'absolute'};
    padding-bottom: ${o =>
      o.$textPlacementMobile === 'bottom' || o.$hasDisclaimer
        ? '73px'
        : o.$textPlacementMobile === 'below' && o.$endsInLink
        ? '24px'
        : o.$textPlacementMobile === 'below'
        ? '48px'
        : null};
  }

  ${fromMd} {
    position: ${o => (o.$textPlacementDesktop === 'center' ? 'absolute' : 'relative')};
    padding: 0 24px ${o => (o.$hasDisclaimer ? '48px' : '0')};
  }

  width: ${o => (o.$textPlacementDesktop === 'center' ? '100%' : '50%')};

  > * {
    margin-left: auto;
    margin-right: auto;
  }

  ${fromXl} {
    padding: 0 24px;
  }
`

const aspectRatioMobileShort = 320 / 253
const aspectRatioMobileTall = 320 / 486
const aspectRatioDesktopSide = 600 / 475
const aspectRatioDesktopSideFullBleed = 720 / 500
const aspectRatioDesktopCenter = 1200 / 475
const aspectRatioDesktopCenterFullBleed = 1440 / 500

export const ContentfulPageBannerSectionAssetWrapper = styled.div<{
  $isFullBleedDesktop: boolean | undefined | null
  $isTallAssetMobile: boolean | undefined | null
  $overlayColorDesktop: string | undefined | null
  $overlayColorMobile: string | undefined | null
  $textPlacementDesktop: string | undefined | null
  $textPlacementMobile: string | undefined | null
}>`
  display: block;
  overflow: hidden;
  padding-bottom: min(
    ${o =>
      (1 /
        (o.$textPlacementMobile !== 'below' || o.$isTallAssetMobile
          ? aspectRatioMobileTall
          : aspectRatioMobileShort)) *
      100}%,
    ${o => (o.$textPlacementMobile !== 'below' || o.$isTallAssetMobile ? '550px' : '350px')}
  );
  position: relative;
  width: 100%;
  > ${ContentfulStyledImageWrapper} {
    height: 0;
  }
  > ${ContentfulStyledImageWrapper}
    > ${ResponsiveImageWrapper},
    > ${ContentfulStyledImageWrapper}
    > ${ResponsiveImageWrapper}
    > picture
    > img,
  > picture > img,
  > img,
  > video {
    height: 100%;
    left: 0;
    object-fit: cover;
    position: absolute;
    top: 0;
    width: 100%;
  }

  ${fromMd} {
    padding-bottom: max(
      min(
        ${o =>
          (1 /
            (o.$textPlacementDesktop === 'center'
              ? o.$isFullBleedDesktop
                ? aspectRatioDesktopCenterFullBleed
                : aspectRatioDesktopCenter
              : (o.$isFullBleedDesktop ? aspectRatioDesktopSideFullBleed : aspectRatioDesktopSide) /
                0.5)) *
          100}%,
        ${o => (o.$isFullBleedDesktop ? '750px' : '700px')}
      ),
      360px
    );
    width: ${o => o.$textPlacementDesktop !== 'center' && '50%'};
  }

  ${o =>
    o.$overlayColorMobile || o.$overlayColorDesktop
      ? `
        &::after {
          background: ${o.$overlayColorMobile};
          bottom: 0;
          content: '';
          left: 0;
          position: absolute;
          right: 0;
          top: 0;

          ${fromMd} {
            background: ${o.$overlayColorDesktop};
            display: ${o.$textPlacementDesktop === 'center' ? 'block' : 'none'};
          }
        }
      `
      : null}
`

export const ContentfulPageBannerSectionAssetOverlayedContent = styled.div`
  left: 50%;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 2 * 24px); /* 24px gap on each side */
  z-index: ${o => o.theme.zIndex.foregroundContent};
`

export const ContentfulPageBannerSectionHotspot = styled.a`
  background: white;
  border-radius: 36px;
  bottom: 24px;
  display: flex;
  height: 36px;
  justify-content: center;
  left: 24px;
  position: absolute;
  width: 36px;
  z-index: 101;

  & > svg {
    transform: translateY(100%);
  }
`

export const ContentfulPageBannerSectionVideoDialogButtonWrapper = styled.div<{
  $textPlacementMobile: string | null | undefined
  $location: 'text' | 'asset'
  $spacing: 'more' | 'less'
  $forceDisplay: boolean
}>`
  display: none;
  ${o => (o.$location === 'asset' && o.$textPlacementMobile === 'below' ? 'display: flex' : null)};
  ${o => (o.$location === 'text' && o.$textPlacementMobile !== 'below' ? 'display: flex' : null)};
  ${o => (o.$forceDisplay ? 'display: flex' : null)};

  ${fromMd} {
    display: none;
    ${o => (o.$location === 'text' ? 'display: flex' : null)};
    ${o => (o.$forceDisplay ? 'display: flex' : null)};
  }

  justify-content: center;

  margin-left: auto;
  margin-right: auto;
  margin: ${o => (o.$spacing === 'more' ? '22px auto 8px' : '16px auto 0px')};
  ${fromMd} {
    margin-top: ${o => (o.$spacing === 'more' ? '32px' : '24px')};
  }
`

export const Disclaimer = styled.div<{
  $placement: PageHeroSection['textPlacementDesktop']
}>`
  ${o =>
    o.$placement === 'center' &&
    `
    position: absolute;
    bottom: ${o.theme.spacing(2)};
    margin-top: 0;
    width: 100%;

    ${fromLg} {
      bottom: ${o.theme.spacing(8)};
      position: absolute;
    }
    `};

  box-sizing: border-box;
  padding: 0 ${o => o.theme.spacing(1)};
  text-align: center;
  color: ${o => o.theme.color.text.primary.on.background};
  margin-top: ${o => o.theme.spacing(6)};
  display: flex;
  justify-content: center;
`
